import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Api from "../utils/api.js";

let ApiContext = React.createContext({});

export function ApiProvider({ children }) {
  const api = React.useMemo(() => {
    // Load api
    const api = new Api("/svc");

    const oldLogin = api.login;
    api.login = (login, password, rememberMe) => {
      return oldLogin.call(api, login, password).then((res) => {
        // Save token for window reloads
        (rememberMe ? localStorage : sessionStorage).setItem("token", res.token);

        // Set info
        setToken(res.token);
        setUser(null);
      });
    };

    api.loginWithToken = (token, rememberMe) => {
      api.token = token;
      return api.reloadProfile().then((result) => {
        if (result) {
          // Save token for window reloads
          (rememberMe ? localStorage : sessionStorage).setItem("token", token);

          // Set info
          setToken(token);
          setUser(null);
        }

        return result;
      });
    };

    const oldLogout = api.logout;
    api.logout = () => {
      return oldLogout.call(api).then(() => {
        setToken(null);
        setUser(null);
      });
    };

    api.reloadProfile = () => {
      return api
        .getProfile()
        .then((user) => {
          setToken(api.token);
          setUser(user);

          return true;
        })
        .catch(() => {
          setToken(null);

          return false;
        });
    };

    return api;
  }, []);

  const [token, setToken] = React.useState(
    sessionStorage.getItem("token") || localStorage.getItem("token")
  );
  const [user, setUser] = React.useState(null);

  React.useEffect(() => {
    if (token) {
      // Enable authenticated API
      api.token = token;

      // Load profile
      api
        .getProfile()
        .then((user) => setUser(user))
        .catch(() => setToken(null));
    } else {
      // Disable authenticated API
      api.token = null;

      // Remove saved token (if any)
      sessionStorage.removeItem("token");
      localStorage.removeItem("token");
    }
  }, [token, api]);

  return <ApiContext.Provider value={{ api, token, user }}>{children}</ApiContext.Provider>;
}

ApiProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useToken() {
  return React.useContext(ApiContext).token;
}

export function useUser() {
  return React.useContext(ApiContext).user;
}

export function useApi() {
  return React.useContext(ApiContext).api;
}

export function RequireAuth({ children }) {
  const token = useToken();
  const user = useUser();
  const location = useLocation();

  // Chargement des données utilisateur
  if (token && !user) {
    return null;
  }

  if (!token) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user.id) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/create-profile" state={{ from: location }} replace />;
  }

  return children;
}

RequireAuth.propTypes = {
  children: PropTypes.node.isRequired,
};
